export type BookingHotelSummary = {
  componentId?: string;
  name: string;
  room: string;
  city: string;
  checkInDate: string;
  checkOutDate: string;
  hotelId?: string;
  hotelCode?: string;
  hotelRef?: string;
  starRating?: string;
  provider?: string;
  cityCode?: string;
  cityName?: string;
  address?: string;
  description?: string;
  mealPlan?: string;
  checkInTime?: string;
  checkOutTime?: string;
  phoneNumber?: string;
  latitude?: number;
  longitude?: number;
  price?: number;
  currency?: string;
  displayPrice?: number;
  displayCurrency?: string;
  thumbnail?: string;
  images?: string[];
  gallery?: string[];
  amenities?: string[];
  roomVariantId?: string;
  roomVariant?: Record<string, unknown> | null;
  voucherUrl?: string;
};

export type BookingFlightSummary = {
  componentId?: string;
  airline: string;
  flightNumber: string;
  from: string;
  to: string;
  departureTime: string;
  arrivalTime: string;
  travelClass: string;
  stops: string;
  stopsCount?: number;
  duration?: string;
  provider?: string;
  price?: number;
  voucherUrl?: string;
};

export type BookingTransferSummary = {
  componentId?: string;
  serviceType: string;
  vehicle: string;
  route: string;
  transferDate: string;
  voucherUrl?: string;
};

export type BookingActivitySummary = {
  componentId?: string;
  title: string;
  city: string;
  serviceDate: string;
  voucherUrl?: string;
};

export type BookingVisaSummary = {
  componentId?: string;
  title: string;
  country: string;
  voucherUrl?: string;
};

export type BookingInsuranceSummary = {
  componentId?: string;
  title: string;
  coverageStartDate: string;
  coverageEndDate: string;
  voucherUrl?: string;
};

type BookingPlanHotelRoomVariant = {
  id?: string | null;
  raw?: Record<string, unknown> | null;
  name?: string | null;
  price?: number | null;
  currency?: string | null;
  offer_id?: string | null;
  refundable?: boolean | null;
  board_basis?: string | null;
};

type BookingPlanHotelMetadata = {
  city?: string | null;
  name?: string | null;
  price?: number | null;
  images?: string[] | null;
  address?: string | null;
  gallery?: string[] | null;
  currency?: string | null;
  hotel_id?: string | null;
  latitude?: number | null;
  provider?: string | null;
  amenities?: string[] | null;
  city_code?: string | null;
  city_name?: string | null;
  hotel_ref?: string | null;
  longitude?: number | null;
  room_type?: string | null;
  thumbnail?: string | null;
  hotel_code?: string | null;
  description?: string | null;
  star_rating?: number | string | null;
  checkin_time?: string | null;
  phone_number?: string | null;
  room_variant?: BookingPlanHotelRoomVariant | null;
  check_in_date?: string | null;
  checkout_time?: string | null;
  display_price?: number | null;
  check_out_date?: string | null;
  room_variant_id?: string | null;
  display_currency?: string | null;
};

type BookingPlanHotelComponent = {
  componentId?: string | null;
  metadata?: BookingPlanHotelMetadata | null;
};

type BookingPlanFlightMetadata = {
  to?: string | null;
  day?: number | null;
  date?: string | null;
  from?: string | null;
  price?: number | null;
  stops?: number | null;
  airline?: string | null;
  duration?: string | null;
  provider?: string | null;
  arrival_time?: string | null;
  flight_number?: string | null;
  departure_time?: string | null;
};

type BookingPlanFlightComponent = {
  componentId?: string | null;
  metadata?: BookingPlanFlightMetadata | null;
};

export type BookingPlan = {
  version: 1 | 2;
  kind: 'package' | 'flight' | 'hotel';
  summary?: {
    title?: string | null;
    travelStartDate?: string | null;
    travelEndDate?: string | null;
    totalAmount?: number;
    currency?: string;
  };
  components?: {
    hotels?: BookingPlanHotelComponent[];
    flights?: BookingPlanFlightComponent[];
    transfers?: Array<Record<string, unknown>>;
    activities?: Array<Record<string, unknown>>;
    visas?: Array<Record<string, unknown>>;
    insurance?: Array<Record<string, unknown>>;
  };
  transfer_calculation?: {
    cab_type?: string | null;
  } | null;
};

export type BookingComponentsSummary = {
  travelStartDate: string;
  travelEndDate: string;
  totalAmount: number;
  hotels: BookingHotelSummary[];
  flights: BookingFlightSummary[];
  transfers: BookingTransferSummary[];
  activities: BookingActivitySummary[];
  visas: BookingVisaSummary[];
  insurance: BookingInsuranceSummary[];
  cabLine: string;
};

type SummaryInput = {
  amount_paise?: unknown;
  amountPaise?: unknown;
  booking_plan?: BookingPlan | null;
  bookingPlan?: BookingPlan | null;
  start_at?: unknown;
  startAt?: unknown;
  travelStartDate?: unknown;
  end_at?: unknown;
  endAt?: unknown;
  travelEndDate?: unknown;
  totalAmount?: unknown;
};

const toText = (value: unknown, fallback = '') => {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  return text || fallback;
};

const toNumber = (value: unknown) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const collectStrings = (...values: unknown[]): string[] => {
  const items: string[] = values.flatMap((value): string[] => {
    if (Array.isArray(value)) return value.flatMap((item): string[] => collectStrings(item));
    const text = toText(value);
    return text ? [text] : [];
  });
  return Array.from(new Set(items));
};

const getVoucherUrl = (value: any) => toText(
  value?.voucherUrl
    || value?.voucher_url
    || value?.pdfUrl
    || value?.pdf_url,
);

const resolveHotelSummary = (hotel: BookingPlanHotelComponent): BookingHotelSummary | null => {
  const metadata = hotel.metadata;
  if (!metadata) return null;

  return {
    componentId: toText(hotel.componentId),
    name: toText(metadata.name),
    room: toText(metadata.room_variant?.name),
    city: toText(metadata.city),
    checkInDate: normalizeDate(metadata.check_in_date),
    checkOutDate: normalizeDate(metadata.check_out_date),
    hotelId: toText(metadata.hotel_id),
    hotelCode: toText(metadata.hotel_code),
    hotelRef: toText(metadata.hotel_ref),
    starRating: toText(metadata.star_rating),
    provider: toText(metadata.provider),
    cityCode: toText(metadata.city_code),
    cityName: toText(metadata.city_name),
    address: toText(metadata.address),
    description: toText(metadata.description),
    mealPlan: toText(metadata.room_variant?.board_basis),
    checkInTime: toText(metadata.checkin_time),
    checkOutTime: toText(metadata.checkout_time),
    phoneNumber: toText(metadata.phone_number),
    latitude: metadata.latitude ?? undefined,
    longitude: metadata.longitude ?? undefined,
    price: metadata.price ?? undefined,
    currency: toText(metadata.currency),
    displayPrice: metadata.display_price ?? undefined,
    displayCurrency: toText(metadata.display_currency),
    thumbnail: toText(metadata.thumbnail),
    images: collectStrings(metadata.images),
    gallery: collectStrings(metadata.gallery),
    amenities: collectStrings(metadata.amenities),
    roomVariantId: toText(metadata.room_variant_id),
    roomVariant: metadata.room_variant?.raw || metadata.room_variant || null,
    voucherUrl: getVoucherUrl(hotel),
  };
};

export const formatMoney = (amount: number) => `₹${Number(amount || 0).toLocaleString('en-IN')}`;

export const normalizeDate = (value: unknown) => {
  if (!value) return '';
  const text = String(value);
  return text.length >= 10 ? text.slice(0, 10) : text;
};

export const formatDisplayDate = (value: string) => {
  const normalized = normalizeDate(value);
  if (!normalized) return '—';
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return normalized;
  return parsed.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

export const formatDisplayDateTime = (value: string) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

const mergeItems = <T,>(
  items: T[],
  isSameItem: (left: T, right: T) => boolean,
  mergeItem: (existing: T, incoming: T) => T,
) => items.reduce<T[]>((acc, item) => {
  const existingIndex = acc.findIndex((existing) => isSameItem(existing, item));
  if (existingIndex === -1) {
    acc.push(item);
    return acc;
  }
  acc[existingIndex] = mergeItem(acc[existingIndex], item);
  return acc;
}, []);

const isMissingValue = (value: unknown) => (
  value === null
  || value === undefined
  || (typeof value === 'string' && !value.trim())
  || (Array.isArray(value) && value.length === 0)
);

const mergePartialRecord = <T extends Record<string, any>>(existing: T, incoming: T): T => {
  const merged = { ...existing } as T;
  const mergedRecord = merged as Record<string, any>;
  const incomingRecord = incoming as Record<string, any>;
  Object.keys(incoming).forEach((key) => {
    if (isMissingValue(mergedRecord[key]) && !isMissingValue(incomingRecord[key])) {
      mergedRecord[key] = incomingRecord[key];
    }
  });
  return merged;
};

const sameValueOrMissing = (left: unknown, right: unknown) => (
  !toText(left)
  || !toText(right)
  || toText(left).toLocaleLowerCase() === toText(right).toLocaleLowerCase()
);

const asRecordArray = (value: unknown): Array<Record<string, any>> => {
  if (Array.isArray(value)) return value.flatMap(asRecordArray);
  return value && typeof value === 'object' ? [value as Record<string, any>] : [];
};

const interimQuotationPlan = (plan: BookingPlan | null | undefined): BookingPlan | null => {
  const quotation = (plan as any)?.quotation;
  if (!quotation || typeof quotation !== 'object' || Array.isArray(quotation)) return null;
  const itinerary = asRecordArray(quotation.itinerary);
  const itineraryItems = (field: string) => itinerary.flatMap((day) => asRecordArray(day[field]));
  const hotels = [...asRecordArray(quotation.hotels), ...itineraryItems('hotels')].map((hotel, index) => ({
    componentId: `hotel-${index + 1}`,
    metadata: {
      ...hotel,
      name: hotel.name || hotel.title || hotel.hotel_name || hotel.hotelName || '',
      city: hotel.city || hotel.city_name || hotel.cityName || '',
      city_name: hotel.city_name || hotel.cityName || hotel.city || '',
      hotel_id: hotel.hotel_id || hotel.hotelId || '',
      hotel_code: hotel.hotel_code || hotel.hotelCode || hotel.hotel_id || hotel.hotelId || '',
      check_in_date: hotel.check_in_date || hotel.checkInDate || hotel.checkin_date || hotel.checkinDate || '',
      check_out_date: hotel.check_out_date || hotel.checkOutDate || hotel.checkout_date || hotel.checkoutDate || '',
      room_variant: hotel.room_variant || hotel.roomVariant || asRecordArray(hotel.room_variants)[0] || asRecordArray(hotel.roomVariants)[0],
    },
  }));
  const flights = [...asRecordArray(quotation.flights), ...itineraryItems('flights')].map((flight, index) => ({
    componentId: `flight-${index + 1}`,
    metadata: {
      ...flight,
      airline: flight.airline || flight.validatingAirline || '',
      flight_number: flight.flight_number || flight.flightNumber || '',
      departure_time: flight.departure_time || flight.departureTime || '',
      arrival_time: flight.arrival_time || flight.arrivalTime || '',
    },
  }));

  return {
    version: 1,
    kind: 'package',
    summary: {
      title: quotation.title || '',
      travelStartDate: quotation.travelStartDate || quotation.startDate || '',
      travelEndDate: quotation.travelEndDate || quotation.endDate || '',
      totalAmount: Number(quotation.price?.base || quotation.totalPrice || 0),
      currency: quotation.price?.currency || quotation.currency || 'INR',
    },
    components: {
      hotels,
      flights,
      transfers: [...asRecordArray(quotation.transfers), ...itineraryItems('transfers')],
      activities: [...asRecordArray(quotation.activities), ...itineraryItems('activities')],
      visas: asRecordArray(quotation.visaDetails || quotation.visa_details),
      insurance: asRecordArray(quotation.insuranceDetails || quotation.insurance_details),
    },
    ...(quotation.transferCalculation || quotation.transfer_calculation
      ? { transfer_calculation: quotation.transferCalculation || quotation.transfer_calculation }
      : {}),
  } as BookingPlan;
};

export const buildBookingComponentsSummaryFromPlan = (plan: BookingPlan | null | undefined): BookingComponentsSummary | null => {
  const normalizedPlan = plan?.components ? plan : interimQuotationPlan(plan);
  if (!normalizedPlan?.components) return null;

  const hotels = mergeItems((normalizedPlan.components.hotels || [])
    .map(resolveHotelSummary)
    .filter((hotel): hotel is BookingHotelSummary => Boolean(hotel?.name)),
  (left, right) => (
    sameValueOrMissing(left.name, right.name)
    && sameValueOrMissing(left.city, right.city)
    && sameValueOrMissing(left.checkInDate, right.checkInDate)
    && sameValueOrMissing(left.checkOutDate, right.checkOutDate)
  ),
  mergePartialRecord);

  const flights = (normalizedPlan.components.flights || [])
    .map<BookingFlightSummary | null>((flight) => {
      const metadata = flight.metadata;
      if (!metadata) return null;

      return {
        componentId: toText(flight.componentId),
        airline: toText(metadata.airline),
        flightNumber: toText(metadata.flight_number),
        from: toText(metadata.from),
        to: toText(metadata.to),
        departureTime: toText(metadata.departure_time),
        arrivalTime: toText(metadata.arrival_time),
        travelClass: '',
        stops: toText(metadata.stops),
        stopsCount: metadata.stops ?? undefined,
        duration: toText(metadata.duration),
        provider: toText(metadata.provider),
        price: metadata.price ?? undefined,
        voucherUrl: getVoucherUrl(flight),
      };
    })
    .filter((flight): flight is BookingFlightSummary => Boolean(flight && (flight.airline || flight.flightNumber || flight.from || flight.to)));

  const transfers = (normalizedPlan.components.transfers || []).map((transfer: any) => ({
    componentId: toText(transfer?.componentId),
    serviceType: toText(transfer?.transfer_type || transfer?.title, 'Cab'),
    vehicle: toText(transfer?.vehicle),
    route: transfer?.from || transfer?.to ? `${toText(transfer?.from, '—')} → ${toText(transfer?.to, '—')}` : '',
    transferDate: normalizeDate(transfer?.transfer_date),
    voucherUrl: getVoucherUrl(transfer),
  })).filter((transfer: BookingTransferSummary) => transfer.serviceType || transfer.vehicle || transfer.route || transfer.transferDate);

  const activities = mergeItems((normalizedPlan.components.activities || []).map((activity: any) => ({
    componentId: toText(activity?.componentId),
    title: toText(activity?.title),
    city: toText(activity?.city),
    serviceDate: normalizeDate(activity?.service_date),
    voucherUrl: getVoucherUrl(activity),
  })).filter((activity: BookingActivitySummary) => activity.title),
  (left, right) => (
    sameValueOrMissing(left.title, right.title)
    && sameValueOrMissing(left.city, right.city)
    && sameValueOrMissing(left.serviceDate, right.serviceDate)
  ),
  mergePartialRecord);

  const visas = (normalizedPlan.components.visas || []).map((visa: any) => ({
    componentId: toText(visa?.componentId),
    title: toText(visa?.title),
    country: toText(visa?.country),
    voucherUrl: getVoucherUrl(visa),
  })).filter((visa: BookingVisaSummary) => visa.title);

  const insurance = (normalizedPlan.components.insurance || []).map((item: any) => ({
    componentId: toText(item?.componentId),
    title: toText(item?.title),
    coverageStartDate: normalizeDate(item?.coverage_start_date),
    coverageEndDate: normalizeDate(item?.coverage_end_date),
    voucherUrl: getVoucherUrl(item),
  })).filter((item: BookingInsuranceSummary) => item.title);

  const cabLine = toText(normalizedPlan.transfer_calculation?.cab_type);

  return {
    travelStartDate: normalizeDate(normalizedPlan.summary?.travelStartDate),
    travelEndDate: normalizeDate(normalizedPlan.summary?.travelEndDate),
    totalAmount: toNumber(normalizedPlan.summary?.totalAmount),
    hotels,
    flights,
    transfers,
    activities,
    visas,
    insurance,
    cabLine,
  };
};

export const buildBookingComponentsSummary = (source: SummaryInput | null | undefined): BookingComponentsSummary | null => {
  if (!source) return null;

  const planSummary = buildBookingComponentsSummaryFromPlan(source.booking_plan || source.bookingPlan || null);
  if (!planSummary) return null;

  const rawAmount = source.amount_paise ?? source.amountPaise ?? source.totalAmount;
  const normalizedAmount = rawAmount !== undefined && rawAmount !== null && toNumber(rawAmount) > 100000
    ? toNumber(rawAmount) / 100
    : toNumber(rawAmount);

  return {
    ...planSummary,
    travelStartDate: planSummary.travelStartDate || normalizeDate(source.start_at || source.startAt || source.travelStartDate || ''),
    travelEndDate: planSummary.travelEndDate || normalizeDate(source.end_at || source.endAt || source.travelEndDate || ''),
    totalAmount: planSummary.totalAmount || normalizedAmount,
  };
};

export const getBookingComponentType = (booking: any): 'hotel' | 'flight' | 'transfer' | 'activity' | 'insurance' | 'visa' | 'other' => {
  const type = toText(
    booking?.product_type
      || booking?.type
      || booking?.category
      || booking?.service
      || booking?.package?.title
      || booking?.title,
    '',
  ).toLowerCase();

  if (type.includes('hotel')) return 'hotel';
  if (type.includes('flight')) return 'flight';
  if (type.includes('transfer') || type.includes('cab')) return 'transfer';
  if (type.includes('activity') || type.includes('tour')) return 'activity';
  if (type.includes('insurance')) return 'insurance';
  if (type.includes('visa')) return 'visa';
  return 'other';
};

export const buildBookingComponentsSummaryFromChildren = (children: any[]): BookingComponentsSummary | null => {
  if (!Array.isArray(children) || children.length === 0) return null;

  const hotelItems: BookingHotelSummary[] = [];
  const flightItems: BookingFlightSummary[] = [];
  const transferItems: BookingTransferSummary[] = [];

  children.forEach((child) => {
    const type = getBookingComponentType(child);
    const componentSummary = buildBookingComponentsSummary({
      bookingPlan: (child as any)?.booking_plan || (child as any)?.bookingPlan,
      travelStartDate: child?.travel_start_date,
      travelEndDate: child?.travel_end_date,
      totalAmount: child?.total_price,
    });

    if (type === 'hotel') {
      if (componentSummary?.hotels.length) {
        hotelItems.push(...componentSummary.hotels);
        return;
      }
      hotelItems.push({
        name: toText(child?.package?.title || child?.title, `Hotel #${child?.id || ''}`),
        room: '',
        city: '',
        checkInDate: normalizeDate(child?.travel_start_date),
        checkOutDate: normalizeDate(child?.travel_end_date),
      });
      return;
    }

    if (type === 'flight') {
      if (componentSummary?.flights.length) {
        flightItems.push(...componentSummary.flights);
        return;
      }
      flightItems.push({
        airline: toText(child?.provider || child?.package?.title || child?.title, ''),
        flightNumber: '',
        from: '',
        to: '',
        departureTime: toText(child?.travel_start_date, ''),
        arrivalTime: toText(child?.travel_end_date, ''),
        travelClass: '',
        stops: '—',
      });
      return;
    }

    if (type === 'transfer') {
      if (componentSummary?.transfers.length) {
        transferItems.push(...componentSummary.transfers);
        return;
      }
      transferItems.push({
        serviceType: 'Cab',
        vehicle: toText(child?.title, ''),
        route: '',
        transferDate: normalizeDate(child?.travel_start_date),
      });
    }
  });

  return {
    travelStartDate: '',
    travelEndDate: '',
    totalAmount: 0,
    hotels: mergeItems(
      hotelItems,
      (left, right) => left.name === right.name && left.room === right.room && left.city === right.city && left.checkInDate === right.checkInDate && left.checkOutDate === right.checkOutDate,
      (existing) => existing,
    ),
    flights: mergeItems(
      flightItems,
      (left, right) => left.airline === right.airline && left.flightNumber === right.flightNumber && left.from === right.from && left.to === right.to && left.departureTime === right.departureTime,
      (existing) => existing,
    ),
    transfers: mergeItems(
      transferItems,
      (left, right) => left.serviceType === right.serviceType && left.vehicle === right.vehicle && left.route === right.route && left.transferDate === right.transferDate,
      (existing) => existing,
    ),
    activities: [],
    visas: [],
    insurance: [],
    cabLine: transferItems[0]?.vehicle || '',
  };
};

export const hasBookingComponentData = (summary: BookingComponentsSummary | null | undefined) => {
  if (!summary) return false;
  return summary.hotels.length > 0
    || summary.flights.length > 0
    || summary.transfers.length > 0
    || summary.activities.length > 0
    || summary.visas.length > 0
    || summary.insurance.length > 0;
};
